import type { JourneyLimits, JourneySubmitValue } from './types.ts';

export type JourneyFormField =
  | 'journey_type_id'
  | 'manager_user_id'
  | 'employee_email'
  | 'employee_name'
  | 'start_date';

/**
 * What is wrong, per field. `running_journey_id` is the refusal for a pair that
 * already has a running journey: it marks the employee field and names the
 * journey to link to. Keep one object per refusal in state, not one per render.
 */
export type JourneyFormErrors = Partial<Record<JourneyFormField, string>> & {
  running_journey_id?: string;
};

export interface JourneyErrorLabels {
  required: string;
  tooLong: (max: number) => string;
  invalidEmail: string;
  /** Beside the link to the running journey: stop that one first. */
  alreadyRunning: string;
}

export interface JourneyDraft {
  journey_type_id: string;
  employee_email: string;
  employee_name: string;
  start_date: string;
}

export const EMPTY_JOURNEY_DRAFT: JourneyDraft = {
  journey_type_id: '',
  employee_email: '',
  employee_name: '',
  start_date: '',
};

/* A shape check only: whether the address exists is for the mail to find out. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** What is wrong with the draft, per field. Empty when it can be sent. */
export function validateJourneyDraft(
  draft: JourneyDraft,
  managerId: string | null,
  labels: JourneyErrorLabels,
  limits: JourneyLimits,
): JourneyFormErrors {
  const errors: JourneyFormErrors = {};

  if (draft.journey_type_id === '') errors.journey_type_id = labels.required;
  if (managerId === null) errors.manager_user_id = labels.required;

  const email = draft.employee_email.trim();
  if (email === '') errors.employee_email = labels.required;
  else if (!EMAIL.test(email)) errors.employee_email = labels.invalidEmail;

  const name = draft.employee_name.trim();
  if (name === '') errors.employee_name = labels.required;
  else if (name.length > limits.name) errors.employee_name = labels.tooLong(limits.name);

  if (!ISO_DATE.test(draft.start_date)) errors.start_date = labels.required;

  return errors;
}

/** The draft as the API takes it. Only call on a draft that validated clean. */
export function toJourneySubmitValue(
  draft: JourneyDraft,
  managerId: string,
): JourneySubmitValue {
  return {
    journey_type_id: draft.journey_type_id,
    manager_user_id: managerId,
    employee_email: draft.employee_email.trim(),
    employee_name: draft.employee_name.trim(),
    start_date: draft.start_date,
  };
}
